import {EntityType, UpdateSocketClientMessage, UpdateSocketServerMessage, User} from "./Model";
import {useAuthStore} from "../app-layout/authStore";
import {rolesOf} from "../util/access/Access";
import {delay} from "../util/concurrency/Delay";
import {SWebSocket, webSocket} from "../util/WebSocket";
import {Lifetime} from "../util/Lifetime";
import {Logger} from "../util/Logger";
import {startHeartbeat} from "../util/Heartbeat";

export type UpdateSocketEvent = {
    type: 'connected';
} | {
    type: 'update';
    entity: EntityType,
    officeId: number | null,
} | {
    type: 'disconnected',
} | {
    type: 'forceRefreshVersion',
    forceRefreshVersion: number,
};

export async function* updateWebSocket({url, lifetime = new Lifetime()}: {
    url: string,
    lifetime?: Lifetime,
}): AsyncGenerator<UpdateSocketEvent, void> {
    const log = Logger('updateWebSocket', {withId: true});

    const reconnectDelay = new ReconnectDelay();

    function parseMessage(message: string): UpdateSocketServerMessage | null {
        try {
            const parsed: UpdateSocketServerMessage = JSON.parse(message);
            return parsed.type === 'pong' || parsed.type === 'entity-update' ? parsed : null;
        } catch (_e) {
            return null;
        }
    }

    function send(ws: SWebSocket, message: UpdateSocketClientMessage) {
        ws.send(JSON.stringify(message));
    }

    log('starting');
    while (lifetime.isAlive) {
        log('next iteration');
        const wsLifetime = lifetime.nested();
        try {
            log('getting token...');
            const token = await nonGuestToken();

            log('opening socket...');
            const ws = await webSocket({url, lifetime: wsLifetime});

            log('reset reconnect delay');
            reconnectDelay.reset();

            log('yielding {connected} event');
            yield {type: 'connected'};

            log('sending auth request to socket...');
            send(ws, {type: 'auth-request', token});

            log('starting heartbeat...');
            const {pong} = startHeartbeat({
                ping: pingTs => send(ws, {type: 'ping', ts: pingTs}),
                lifetime: wsLifetime,
            });

            log('iterating over messages...');
            for await (const message of ws.incoming) {
                const parsedMessage = parseMessage(message);
                switch (parsedMessage?.type) {
                    case undefined:
                        log('ignoring message:', message);
                        break;
                    case 'entity-update':
                        log('received entity-update:', parsedMessage);
                        yield {type: 'update', entity: parsedMessage.entity, officeId: parsedMessage.officeId};
                        break;
                    case 'pong':
                        log('received pong:', parsedMessage);
                        pong(parsedMessage.ts);
                        yield {type: 'forceRefreshVersion', forceRefreshVersion: parsedMessage.forceRefreshVersion};
                        break;
                }
            }
            log("closed normally");
        } catch (err) {

            log.error("ws bites the dust", err);
        } finally {
            wsLifetime.terminate();
            log('yielding {disconnected} event');
            yield {type: 'disconnected'};
        }

        if (lifetime.isAlive) {
            log(`waiting for reconnect delay...`);
            await reconnectDelay.delay();
        }
    }
    log('terminated');
}

function nonGuestToken(): Promise<string> {
    function isNotGuest(user: User | null) {
        return rolesOf(user).some(role => role.type !== 'guest');
    }

    const {token, user} = useAuthStore.getState();
    return new Promise(resolve => {
        if (token && isNotGuest(user)) {
            resolve(token);
        } else {
            const unsubscribe = useAuthStore.subscribe(({token, user}) => {
                if (token && isNotGuest(user)) {
                    unsubscribe();
                    resolve(token);
                }
            })
        }
    });
}

class ReconnectDelay {
    private _delay: number = this.initialDelay;

    constructor(
        private initialDelay: number = 1_000,
        private maxReconnectDelay: number = 30_000,
        private jitter: number = 0.2,
    ) {
    }

    reset() {
        this._delay = this.initialDelay;
    }

    async delay(): Promise<void> {
        const jitteredDelay = Math.round(this._delay * (1 + (Math.random() * 2 - 1) * this.jitter));
        this._delay = Math.min(this._delay * 2, this.maxReconnectDelay);
        await delay(jitteredDelay);
    }
}
